/**
 * Wheel Finder Quiz
 * Recommendation engine and cart integration
 */

(function() {
  'use strict';

  const routines = {
    'desk-shoulders': [
      'Passive Thoracic Extension (2 min)',
      'Shoulder Blade Squeeze over Wheel (1 min)',
      'Neck Traction Roll (30 sec each side)'
    ],
    'backbends': [
      'Supported Fish Pose (3 min)',
      'Bridge with Wheel Support (5 breaths)',
      'Wheel-Assisted Urdhva Dhanurasana (30 sec)'
    ],
    'targeted-release': [
      'Pinpoint Pressure on Upper Back (2 min)',
      'Hip Flexor Release with Wheel Prop (1 min each)',
      'Lat Stretch over Wheel (1 min each side)'
    ],
    'travel': [
      'Quick Spine Decompress (1 min)',
      'Desk Recovery Stretch (1 min)',
      'Morning Wake-Up Roll (1 min)'
    ],
    'acrobatics': [
      'Hollow Body Wheel Balance (1 min)',
      'Forearm Stand Prep with Wheel (2 min)',
      'Backbend Flow Sequence (3 min)'
    ]
  };

  const sizeReasons = {
    '9': {
      'travel': 'The 9" compact wheel fits in any carry-on and is perfect for on-the-go relief.',
      'targeted-release': 'The 9" wheel provides precise, targeted pressure on specific muscle groups.',
      'default': 'The 9" compact wheel matches your height for comfortable, controlled stretching.'
    },
    '12': {
      'desk-shoulders': 'The 12" wheel is ideal for opening desk-bound shoulders with its versatile arc.',
      'default': 'The 12" wheel is the most versatile choice—perfect for your height and goals.'
    },
    '15': {
      'backbends': 'The 15" wheel provides the deepest backbend arc for dramatic spinal extension.',
      'acrobatics': 'The 15" wheel offers maximum stability for advanced inversions and balances.',
      'default': 'The 15" wheel matches your height for deeper, more supported stretches.'
    }
  };

  class WheelFinder {
    constructor(container) {
      this.container = container;
      this.answers = {};
      this.currentStep = 1;

      this.elements = {
        steps: container.querySelectorAll('[data-step]'),
        progressDots: container.querySelectorAll('.wheel-finder__progress-dot'),
        results: container.querySelector('[data-results]'),
        resultSize: container.querySelector('[data-result-size]'),
        resultWhy: container.querySelector('[data-result-why]'),
        resultSafety: container.querySelector('[data-result-safety]'),
        resultDisclaimer: container.querySelector('[data-result-disclaimer]'),
        resultRoutine: container.querySelector('[data-result-routine]'),
        resultPrice: container.querySelector('[data-result-price]'),
        addToCartBtn: container.querySelector('[data-add-to-cart]')
      };

      this.bindEvents();
    }

    bindEvents() {
      this.container.querySelectorAll('.wheel-finder__option').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleOptionClick(e));
      });

      this.container.querySelectorAll('[data-back]').forEach(btn => {
        btn.addEventListener('click', (e) => this.handleBackClick(e));
      });

      this.container.querySelectorAll('[data-restart]').forEach(btn => {
        btn.addEventListener('click', () => this.restart());
      });

      if (this.elements.addToCartBtn) {
        this.elements.addToCartBtn.addEventListener('click', () => this.addToCart());
      }
    }

    handleOptionClick(e) {
      const btn = e.currentTarget;
      const value = btn.dataset.value;
      const step = btn.closest('[data-step]');
      const stepNum = parseInt(step.dataset.step);

      const stepKeys = ['height', 'goal', 'experience', 'spinal'];
      this.answers[stepKeys[stepNum - 1]] = value;

      btn.closest('.wheel-finder__options').querySelectorAll('.wheel-finder__option').forEach(opt => {
        opt.classList.remove('selected');
      });
      btn.classList.add('selected');

      if (btn.dataset.finish === 'true') {
        setTimeout(() => this.showResults(), 300);
      } else if (btn.dataset.next) {
        setTimeout(() => this.goToStep(parseInt(btn.dataset.next)), 300);
      }
    }

    handleBackClick(e) {
      const targetStep = parseInt(e.currentTarget.dataset.back);
      this.goToStep(targetStep);
    }

    goToStep(stepNum) {
      this.container.querySelectorAll('.wheel-finder__step').forEach(step => {
        step.classList.remove('active');
      });

      const targetStep = this.container.querySelector(`.wheel-finder__step[data-step="${stepNum}"]`);
      if (targetStep) {
        targetStep.classList.add('active');
        this.currentStep = stepNum;
        this.updateProgress(stepNum);
      }
    }

    updateProgress(stepNum) {
      this.elements.progressDots.forEach((dot, i) => {
        dot.classList.toggle('active', i < stepNum);
        dot.classList.toggle('current', i === stepNum - 1);
      });
    }

    calculateRecommendation() {
      const { height, goal, experience, spinal } = this.answers;

      let size = '12';
      if (height === 'short') size = '9';
      else if (height === 'tall') size = '15';

      if (goal === 'travel') size = '9';
      if (goal === 'targeted-release' && size !== '15') size = '9';

      if (goal === 'backbends' && (experience === 'intermediate' || experience === 'advanced')) {
        if (height !== 'short') size = '15';
      }
      if (goal === 'acrobatics' && experience === 'advanced') {
        if (height !== 'short') size = '15';
      }

      if (experience === 'beginner' && size === '15') {
        size = '12';
      }

      return {
        size,
        showDisclaimer: spinal === 'yes',
        routine: routines[goal] || routines['desk-shoulders'],
        reason: this.getReason(size, goal)
      };
    }

    getReason(size, goal) {
      const sizeReasonMap = sizeReasons[size];
      return sizeReasonMap[goal] || sizeReasonMap['default'];
    }

    showResults() {
      const rec = this.calculateRecommendation();
      const variantData = window.wheelFinderData?.variants?.[rec.size];

      this.recommendedVariantId = variantData?.id;

      if (this.elements.resultSize) {
        this.elements.resultSize.textContent = rec.size + '"';
      }
      if (this.elements.resultWhy) {
        this.elements.resultWhy.textContent = rec.reason;
      }
      if (this.elements.resultPrice && variantData) {
        this.elements.resultPrice.textContent = variantData.price;
      }
      if (this.elements.resultRoutine) {
        this.elements.resultRoutine.innerHTML = rec.routine.map(step => `<li>${step}</li>`).join('');
      }
      if (this.elements.resultDisclaimer) {
        this.elements.resultDisclaimer.hidden = !rec.showDisclaimer;
      }

      this.container.querySelectorAll('.wheel-finder__step').forEach(step => {
        step.classList.remove('active');
      });

      if (this.elements.results) {
        this.elements.results.hidden = false;
        this.elements.results.classList.add('active');
      }

      this.updateProgress(5);
    }

    async addToCart() {
      if (!this.recommendedVariantId) {
        console.error('No variant ID available');
        return;
      }

      const btn = this.elements.addToCartBtn;
      const originalText = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<span>Adding...</span>';

      try {
        const formData = new FormData();
        formData.append('id', this.recommendedVariantId);
        formData.append('quantity', 1);

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cart) {
          formData.append('sections', cart.getSectionsToRender().map(s => s.id));
          formData.append('sections_url', window.location.pathname);
        }

        const response = await fetch(window.wheelFinderData.cartAddUrl, {
          method: 'POST',
          body: formData
        });

        const data = await response.json();

        if (data.status === 422) {
          throw new Error(data.description || 'Could not add to cart');
        }

        btn.innerHTML = '<span>Added!</span>';

        if (cart) {
          cart.renderContents(data);
        } else {
          window.location.href = '/cart';
        }

        setTimeout(() => {
          btn.innerHTML = originalText;
          btn.disabled = false;
        }, 2000);

      } catch (error) {
        console.error('Add to cart error:', error);
        btn.innerHTML = '<span>Error - Try Again</span>';
        btn.disabled = false;
        setTimeout(() => {
          btn.innerHTML = originalText;
        }, 2000);
      }
    }

    restart() {
      this.answers = {};
      this.currentStep = 1;
      this.recommendedVariantId = null;

      this.container.querySelectorAll('.wheel-finder__option').forEach(opt => {
        opt.classList.remove('selected');
      });

      if (this.elements.results) {
        this.elements.results.hidden = true;
        this.elements.results.classList.remove('active');
      }

      this.goToStep(1);
    }
  }

  function init() {
    const container = document.querySelector('[data-quiz-container]');
    if (container) {
      new WheelFinder(container);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
